import { sha256 } from "viem";
import type { Hex } from "viem";

/**
 * The extension package (D71, D77): a zip with manifest.json at its root and the files the manifest lists, each with
 * its sha256. The chain holds the sha256 of manifest.json and the url the host serves the version from; the Console
 * and the host check the same hashes, the Console before the upload and at install, the host at the upload.
 */
export const EXTENSION_API = 1;

export type WalletPermission = "authorize";
export type ReadPermission = "balances" | "authorizations";

export interface ExtensionFile {
  path: string;
  sha256: string; // hex without 0x
  size: number;
}

export interface ExtensionManifest {
  id: string; // handle/name, for example steven@68df/film-editor
  name: string;
  version: string; // major.minor.patch
  description: string;
  icon?: string;
  api: number;
  entry: string;
  files: ExtensionFile[];
  services: string[];
  storage: { config?: string; store?: string };
  permissions: { wallet: WalletPermission[]; read: ReadPermission[]; notify: boolean };
  locales: string[];
}

/** One file of a package, with the hash the manifest declares for it (undefined: not declared). */
export interface PackageFile {
  path: string;
  size: number;
  sha256: string;
  declared?: string;
}

export type PackageProblemCode =
  | "manifest"
  | "id"
  | "handle"
  | "api"
  | "semver"
  | "entry"
  | "undeclared"
  | "mismatch"
  | "missing"
  | "size"
  | "path" // a zip path that leaves the package: `..`, a leading slash, a backslash, an empty name
  | "name"; // the manifest's name is empty

export interface PackageProblem {
  code: PackageProblemCode;
  path?: string;
  detail?: string;
}

export interface PackageCheck {
  manifest: ExtensionManifest | null;
  /** sha256 of manifest.json as the chain holds it (0x-prefixed), null without a manifest. */
  hash: Hex | null;
  files: PackageFile[];
  problems: PackageProblem[];
}

/** The largest file of a package, and the largest package: the host keeps the bytes in Durable Object storage. */
export const PACKAGE_FILE_LIMIT = 4 * 1024 * 1024;
export const PACKAGE_LIMIT = 8 * 1024 * 1024;
/** The most a configuration namespace may hold, whatever the manifest asks (D74). */
export const CONFIG_QUOTA_CEILING = 64 * 1024;

export function sha256Hex(bytes: Uint8Array): Hex {
  return sha256(bytes);
}

/** The first 16 hex characters of a hash: the version's segment of a package url. */
export function hash16(hash: Hex): string {
  return hash.slice(2, 18).toLowerCase();
}

const NAME = /^[a-z][a-z0-9-]*$/;

/** `handle/name` → the handle (`name@addr4`) and the extension's name; null when the id is not well formed. */
export function parseExtensionId(
  id: string,
): { handle: string; handleName: string; addr4: string; name: string } | null {
  const m = /^([a-z][a-z0-9-]{2,15})@([0-9a-f]{4})\/([a-z][a-z0-9-]{2,31})$/.exec(id);
  if (!m) return null;
  return { handle: `${m[1]}@${m[2]}`, handleName: m[1]!, addr4: m[2]!, name: m[3]! };
}

/** The handle as a path segment of a package url: `steven@68df` → `steven-68df`. */
export function handlePath(handle: string): string {
  return handle.replace("@", "-");
}

/** The handle from its path segment: the addr4 is after the last hyphen. */
export function handleFromPath(segment: string): string | null {
  const i = segment.lastIndexOf("-");
  if (i < 3) return null;
  const name = segment.slice(0, i);
  const addr4 = segment.slice(i + 1);
  if (!NAME.test(name) || name.length > 16 || !/^[0-9a-f]{4}$/.test(addr4)) return null;
  return `${name}@${addr4}`;
}

/** Where the host serves a version: `<origin>/ext/<handle-path>/<name>/v/<hash16>/` (a version path, D70). */
export function packageUrl(hostOrigin: string, id: string, hash: Hex): string {
  const parsed = parseExtensionId(id);
  if (!parsed) throw new Error(`bad extension id ${id}`);
  return `${hostOrigin.replace(/\/$/, "")}/ext/${handlePath(parsed.handle)}/${parsed.name}/v/${hash16(hash)}/`;
}

/** A quota of the manifest, `8 KiB` or `256 MiB`, in bytes; null when it is not one. */
export function parseQuota(text: string | undefined): number | null {
  if (!text) return null;
  const m = /^(\d+(?:\.\d+)?)\s*(B|KiB|MiB|GiB)$/.exec(text.trim());
  if (!m) return null;
  const unit = { B: 1, KiB: 1024, MiB: 1024 * 1024, GiB: 1024 * 1024 * 1024 }[m[2] as "B" | "KiB" | "MiB" | "GiB"];
  return Math.round(Number(m[1]) * unit);
}

/** major.minor.patch, compared number by number: negative when a < b, zero when equal. */
export function compareVersions(a: string, b: string): number {
  const pa = a.split(".").map(Number);
  const pb = b.split(".").map(Number);
  for (let i = 0; i < 3; i++) {
    const d = (pa[i] ?? 0) - (pb[i] ?? 0);
    if (d !== 0) return d;
  }
  return 0;
}

const SEMVER = /^\d+\.\d+\.\d+$/;

/**
 * Checks a package: the manifest at its root, every file's sha256 against the manifest's `files`, the entry file,
 * the id under `handle` when given, the api version. The same check runs in the browser before the upload and in
 * the host at the upload; `entries` are the files of the zip by path (directories left out).
 */
/** A path inside a package: relative, no `..` segment, no backslash, not empty. */
export function validPackagePath(path: string): boolean {
  return path !== "" && !path.startsWith("/") && !path.includes("\\") && !path.split("/").some((s) => s === "..");
}

/**
 * The filter of fflate's unzip: an entry inflates only when the size the zip's directory declares is within the
 * limits (per file and in total), so a zip bomb never inflates; the entries dropped are the problems to report.
 * Directory entries are skipped without a problem.
 */
export function packageUnzipFilter(): {
  filter: (f: { name: string; originalSize: number }) => boolean;
  dropped: PackageProblem[];
} {
  const dropped: PackageProblem[] = [];
  let total = 0;
  return {
    dropped,
    filter(f) {
      if (f.name.endsWith("/")) return false;
      if (f.originalSize > PACKAGE_FILE_LIMIT) {
        dropped.push({ code: "size", path: f.name });
        return false;
      }
      total += f.originalSize;
      if (total > PACKAGE_LIMIT) {
        dropped.push({ code: "size", path: f.name });
        return false;
      }
      return true;
    },
  };
}

export function checkPackage(entries: Record<string, Uint8Array>, opts: { handle?: string } = {}): PackageCheck {
  const problems: PackageProblem[] = [];
  const manifestBytes = entries["manifest.json"];
  if (!manifestBytes) return { manifest: null, hash: null, files: [], problems: [{ code: "manifest" }] };
  let manifest: ExtensionManifest;
  try {
    manifest = JSON.parse(new TextDecoder().decode(manifestBytes)) as ExtensionManifest;
    if (!manifest || typeof manifest !== "object") throw new Error("not an object");
  } catch (e) {
    return {
      manifest: null,
      hash: null,
      files: [],
      problems: [{ code: "manifest", detail: e instanceof Error ? e.message : String(e) }],
    };
  }
  const hash = sha256Hex(manifestBytes);
  for (const path of Object.keys(entries)) if (!validPackagePath(path)) problems.push({ code: "path", path });
  if (typeof manifest.name !== "string" || manifest.name.trim() === "") problems.push({ code: "name" });
  const declared = Array.isArray(manifest.files) ? manifest.files : [];
  const files: PackageFile[] = Object.entries(entries)
    .filter(([path]) => path !== "manifest.json")
    .map(([path, bytes]) => ({
      path,
      size: bytes.length,
      sha256: sha256Hex(bytes).slice(2),
      declared: declared.find((d) => d.path === path)?.sha256?.toLowerCase(),
    }));
  const parsed = typeof manifest.id === "string" ? parseExtensionId(manifest.id) : null;
  if (!parsed) problems.push({ code: "id", detail: String(manifest.id) });
  else if (opts.handle && parsed.handle !== opts.handle) problems.push({ code: "handle", detail: opts.handle });
  if (manifest.api !== EXTENSION_API) problems.push({ code: "api", detail: String(manifest.api) });
  if (typeof manifest.version !== "string" || !SEMVER.test(manifest.version)) problems.push({ code: "semver" });
  if (typeof manifest.entry !== "string" || !files.some((f) => f.path === manifest.entry))
    problems.push({ code: "entry", path: String(manifest.entry) });
  let total = manifestBytes.length;
  for (const f of files) {
    total += f.size;
    if (f.size > PACKAGE_FILE_LIMIT) problems.push({ code: "size", path: f.path });
    if (!f.declared) problems.push({ code: "undeclared", path: f.path });
    else if (f.declared !== f.sha256) problems.push({ code: "mismatch", path: f.path });
  }
  if (total > PACKAGE_LIMIT) problems.push({ code: "size" });
  for (const d of declared) if (!files.some((f) => f.path === d.path)) problems.push({ code: "missing", path: d.path });
  return { manifest, hash, files, problems };
}

/**
 * The bytes a configuration namespace may hold: the manifest's `storage.config`, bounded by the ceiling; null when the
 * manifest declares no configuration.
 */
export function configQuotaOf(m: Pick<ExtensionManifest, "storage">): number | null {
  const q = parseQuota(m.storage?.config);
  return q === null ? null : Math.min(q, CONFIG_QUOTA_CEILING);
}
